import React, { useState, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import './Templates.css';

function flatten(categories = {}) {
  const items = [];
  Object.entries(categories || {}).forEach(([cat, arr]) => {
    (arr || []).forEach((item) => items.push({
      description: item.name || item,
      category: cat,
      qty: item.quantity ?? item.qty ?? 1,
      rate: item.rate ?? 0,
    }));
  });
  return items;
}

function slugify(str) {
  return (str || '')
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

function EditModal({ visible, onClose, templateKey, data, onSave, isNew, categoryOptions = [] }) {
  const [name, setName] = useState(data?.name || '');
  const [description, setDescription] = useState(data?.description || '');
  const [active, setActive] = useState(data?.active ?? true);
  const [items, setItems] = useState(() => flatten(data?.categories));
  const [selectedCategory, setSelectedCategory] = useState(
    () => Object.keys(data?.categories || {})[0] || categoryOptions[0] || 'General'
  );
  const [error, setError] = useState('');

  React.useEffect(() => {
    if (visible) {
      setName(data?.name || '');
      setDescription(data?.description || '');
      setActive(data?.active ?? true);
      setItems(flatten(data?.categories));
      setSelectedCategory(Object.keys(data?.categories || {})[0] || categoryOptions[0] || 'General');
      setError('');
    }
  }, [visible, data, categoryOptions]);

  const addItem = () => setItems((prev) => [...prev, { description: '', category: selectedCategory || '', qty: 1, rate: 0 }]);
  const updateItem = (i, next) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, ...next } : it)));
  const removeItem = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const handleSave = () => {
    try {
      const categories = {};
      items.forEach((it) => {
        const cat = it.category || selectedCategory || 'General';
        if (!categories[cat]) categories[cat] = [];
        categories[cat].push({ name: it.description || '', rate: Number(it.rate) || 0, quantity: Number(it.qty) || 1 });
      });

      if (isNew) {
        const key = slugify(name) || `template-${Date.now()}`;
        onSave(key, { name, description, categories, active }, { isNew: true });
      } else {
        onSave(templateKey, { name, description, categories, active });
      }
      onClose();
    } catch (e) {
      setError('Failed to save template');
    }
  };

  if (!visible) return null;

  return (
    <div className="templates-modal-overlay">
      <div className="templates-modal-dialog">
        <div className="templates-modal-header">
          <h3>{isNew ? 'Create New Template' : `Edit: ${templateKey}`}</h3>
          <div className="templates-modal-actions">
            <button className="templates-btn templates-btn--ghost" onClick={onClose}>Close</button>
            <button className="templates-btn templates-btn--primary" onClick={handleSave}>{isNew ? 'Create Template' : 'Save'}</button>
          </div>
        </div>

        <div className="templates-modal-grid">
          <div>
            <label className="templates-label">Template Name *</label>
            <input className="templates-input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className="templates-label">Category *</label>
            <select className="templates-input" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
              {(categoryOptions || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
              <option value="">Other / Custom</option>
            </select>
          </div>
        </div>

        <div className="templates-block">
          <label className="templates-label">Description</label>
          <textarea className="templates-input" value={description} onChange={(e) => setDescription(e.target.value)} />
          <div className="templates-toggle-row">
            <label className="templates-toggle">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              <span>Active (show in invoice creation)</span>
            </label>
          </div>
        </div>

        <h4 className="templates-subtitle">Available Options (Add all possible items)</h4>
        <p className="templates-subtext">Add every possible item for this project type. Users can pick and choose when creating invoices.</p>

        <div className="templates-items-wrapper">
          <div className="templates-items-grid templates-items-header-row">
            <div><strong>DESCRIPTION</strong></div>
            <div><strong>CATEGORY</strong></div>
            <div className="templates-center"><strong>QTY</strong></div>
            <div className="templates-center"><strong>RATE</strong></div>
            <div className="templates-right"><strong>AMOUNT</strong></div>
          </div>

          {items.map((it, idx) => (
            <div key={idx} className="templates-items-grid templates-items-row">
              <input className="templates-input" value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value })} />
              <input className="templates-input" value={it.category} onChange={(e) => updateItem(idx, { category: e.target.value })} />
              <input className="templates-input templates-input--center" type="number" value={it.qty} onChange={(e) => updateItem(idx, { qty: Number(e.target.value) })} />
              <input className="templates-input templates-input--center" type="number" value={it.rate} onChange={(e) => updateItem(idx, { rate: Number(e.target.value) })} />
              <div className="templates-amount-cell">
                <div className="templates-amount">${((Number(it.qty) || 0) * (Number(it.rate) || 0)).toFixed(2)}</div>
                <button className="templates-remove-btn" onClick={() => removeItem(idx)}>Remove</button>
              </div>
            </div>
          ))}

          <div className="templates-add-line-wrap">
            <button className="templates-add-line-btn" onClick={addItem}>+ Add Line Item</button>
          </div>
        </div>

        {error && <div className="templates-error">{error}</div>}
      </div>
    </div>
  );
}

export default function Templates() {
  const { templates = {}, addTemplate, updateTemplate, removeTemplate } = useApp();
  const entries = useMemo(() => Object.entries(templates || {}), [templates]);
  const [editing, setEditing] = useState(null);
  const [editingData, setEditingData] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalIsNew, setModalIsNew] = useState(false);

  const [showCalculator, setShowCalculator] = useState(false);
  const [calcNetRate, setCalcNetRate] = useState('');
  const [calcPaymentMethod, setCalcPaymentMethod] = useState('card');
  const [calcResult, setCalcResult] = useState(null);
  const [message, setMessage] = useState(null);

  const calculateClientRate = () => {
    const netRate = parseFloat(calcNetRate) || 0;
    if (netRate <= 0) {
      setCalcResult(null);
      return;
    }
    let feePercent = 0;
    if (calcPaymentMethod === 'card') feePercent = 0.029;
    if (calcPaymentMethod === 'ach') feePercent = 0.008;
    const feeAmount = Math.round((netRate * feePercent) + 0.30) / 100;
    const clientRate = netRate + feeAmount;
    setCalcResult({ netRate, feeAmount, clientRate: clientRate.toFixed(2) });
  };

  const categoryOptions = useMemo(() => {
    const set = new Set();
    Object.values(templates || {}).forEach((t) => {
      Object.keys(t.categories || {}).forEach((c) => set.add(c));
    });
    ['Planning', 'Design', 'Development', 'Testing', 'Integration', 'General'].forEach((d) => set.add(d));
    return Array.from(set);
  }, [templates]);

  const createNew = () => {
    setEditing(null);
    setEditingData(null);
    setModalIsNew(true);
    setModalVisible(true);
  };

  const openEdit = (key, data) => {
    setEditing(key);
    setEditingData(data);
    setModalIsNew(false);
    setModalVisible(true);
  };

  const handleSave = (key, payload, opts = {}) => {
    if (opts?.isNew) {
      let finalKey = key;
      let i = 1;
      while (templates[finalKey]) {
        finalKey = `${key}-${i++}`;
      }
      addTemplate(finalKey, payload);
    } else if (templates[key]) {
      updateTemplate(key, payload);
    } else {
      addTemplate(key, payload);
    }
  };

  const handleDuplicate = (key, data) => {
    let copyKey = `${key}-copy`;
    let i = 1;
    while (templates[copyKey]) copyKey = `${key}-copy-${i++}`;
    addTemplate(copyKey, { ...(data || {}), name: `${data?.name || key} (copy)` });
  };

  const handleDelete = (key) => {
    if (!window.confirm(`Delete template "${key}"?`)) return;
    removeTemplate(key);
  };

  return (
    <div className="templates-page">
      <div className="templates-head">
        <div>
          <h2>Project Templates</h2>
          <p>Create templates for faster invoice generation</p>
        </div>
        <div className="templates-head-actions">
          <button className="templates-btn templates-btn--ghost" onClick={() => setShowCalculator(!showCalculator)}>Rate Calculator</button>
          <button className="templates-btn templates-btn--primary" onClick={createNew}>+ New Template</button>
        </div>
      </div>

      {showCalculator && (
        <div className="templates-calc-card">
          <div className="templates-calc-head">
            <h3>Rate Calculator (Net + Fees = Client Rate)</h3>
            <button className="templates-calc-close" onClick={() => setShowCalculator(false)}>x</button>
          </div>
          <div className="templates-calc-grid">
            <div>
              <label className="templates-label">Your Net Rate ($)</label>
              <input
                className="templates-input"
                type="number"
                value={calcNetRate}
                onChange={(e) => setCalcNetRate(e.target.value)}
                placeholder="e.g. 300.00"
              />
            </div>
            <div>
              <label className="templates-label">Payment Method</label>
              <select className="templates-input" value={calcPaymentMethod} onChange={(e) => setCalcPaymentMethod(e.target.value)}>
                <option value="card">Credit Card (2.9% + $0.30)</option>
                <option value="ach">ACH (0.8% + $0.30)</option>
              </select>
            </div>
            <div className="templates-calc-btn-wrap">
              <button className="templates-btn templates-btn--success" onClick={calculateClientRate}>Calculate</button>
            </div>
          </div>
          {calcResult && (
            <div className="templates-calc-result">
              <div><strong>Your Net Rate:</strong> ${Number(calcResult.netRate).toFixed(2)}</div>
              <div><strong>Processing Fees:</strong> ${Number(calcResult.feeAmount).toFixed(2)}</div>
              <div className="templates-calc-total"><strong>Client Pays (use this in template):</strong> ${calcResult.clientRate}</div>
              <button
                className="templates-btn templates-btn--soft"
                onClick={() => {
                  navigator.clipboard.writeText(calcResult.clientRate);
                  setMessage({ type: 'success', text: 'Client rate copied to clipboard' });
                  setTimeout(() => setMessage(null), 3000);
                }}
              >
                Copy Rate
              </button>
              {message && <div className="templates-calc-message">{message.text}</div>}
            </div>
          )}
        </div>
      )}

      <div className="templates-grid">
        {entries.map(([key, data]) => {
          const categories = data?.categories || {};
          const optionCount = Object.values(categories).reduce((s, arr) => s + (Array.isArray(arr) ? arr.length : 0), 0);
          const firstCat = Object.keys(categories)[0];
          const previewItems = (firstCat && Array.isArray(categories[firstCat])) ? categories[firstCat].slice(0, 2) : [];
          return (
            <div key={key} className="template-card">
              <div className="template-card-head">
                <div>
                  <div className="template-card-title">{data?.name || key}</div>
                  <div className="template-card-desc">{data?.description || ''}</div>
                </div>
                <div className="template-card-actions">
                  <button className="template-link-btn" onClick={() => openEdit(key, data)}>Edit</button>
                  <button className="template-link-btn" onClick={() => handleDuplicate(key, data)}>Copy</button>
                  <button className="template-link-btn template-link-btn--danger" onClick={() => handleDelete(key)}>Delete</button>
                </div>
              </div>

              <div className="template-card-meta">{optionCount} available options</div>
              <ul className="template-preview-list">
                {previewItems.map((it, idx) => (
                  <li key={idx}>{it.name || it}</li>
                ))}
                {optionCount > previewItems.length && <li className="template-preview-more">+{optionCount - previewItems.length} more</li>}
              </ul>
            </div>
          );
        })}
      </div>

      <EditModal
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          setModalIsNew(false);
        }}
        templateKey={editing}
        data={editingData}
        onSave={handleSave}
        isNew={modalIsNew}
        categoryOptions={categoryOptions}
      />
    </div>
  );
}
